import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Copy, Check, ExternalLink, Banknote, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { getClientInvoiceDetail } from '../functions/api_fethcers';
import { getClientPaymentMethods } from '../functions/portalBillingApi';
import { payInvoiceWithSavedCard, postPaymentStatus } from '../functions/paymentApi';
import PaymentForm from './PaymentForm';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const PaymentModal = ({
  isOpen,
  onClose,
  invoice,
  createPaymentIntent,
  isCreatingPaymentIntent,
  onPaymentSuccess,
}) => {
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeMethod, setActiveMethod] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [savedMethods, setSavedMethods] = useState([]);
  const [useNewCard, setUseNewCard] = useState(false);
  const [payingWith, setPayingWith] = useState(null);
  const dialogRef = useRef(null);

  // A charge is in flight whenever a payment intent is being created or a
  // saved card is being charged. The modal must not be dismissible during
  // this window (backdrop click, X button, Escape) so a client can't walk
  // away mid-charge and lose track of whether they paid.
  const isProcessing = isCreatingPaymentIntent || !!payingWith;

  // Fetch full invoice detail when modal opens
  useEffect(() => {
    if (isOpen && invoice?._id) {
      setLoading(true);
      setActiveMethod(null);
      setClientSecret(null);
      setUseNewCard(false);
      setPayingWith(null);
      setSavedMethods([]);
      // Load any cards the client already has on file (for quick pay).
      getClientPaymentMethods()
        .then((res) => setSavedMethods(res?.paymentMethods || []))
        .catch(() => setSavedMethods([]));
      getClientInvoiceDetail(invoice._id)
        .then((detail) => {
          setInvoiceDetail(detail);
          // Auto-select first available method
          const methods = getAvailableMethods(detail?.payment_options);
          if (methods.length === 1) {
            setActiveMethod(methods[0].id);
          }
        })
        .catch((err) => {
          toast.error(err.message || 'Failed to load invoice details');
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, invoice?._id]);

  // Move focus into the dialog ONCE, when it opens.
  //
  // This used to live in the keydown effect below, whose deps include `onClose`.
  // Both parents pass `onClose={() => ...}`, a fresh arrow every parent render, so
  // that effect re-ran constantly and called dialogEl.focus() each time. Clicking
  // into the Stripe card field blurs the window (the field is a cross-origin
  // iframe), react-query's refetchOnWindowFocus then re-renders the parent, and the
  // refocus yanked the caret straight back out of the card. The card looked dead:
  // you could not click or type into it. Focusing only on open leaves the iframe
  // alone.
  useEffect(() => {
    if (!isOpen) return;
    dialogRef.current?.focus();
  }, [isOpen]);

  // Escape-to-close and the Tab trap, guarded by isProcessing so a client can't
  // dismiss the dialog mid-charge with the keyboard either. This effect must never
  // move focus: it re-runs whenever isProcessing or onClose changes identity.
  useEffect(() => {
    if (!isOpen) return;

    const dialogEl = dialogRef.current;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (!isProcessing) onClose();
        return;
      }
      if (e.key === 'Tab' && dialogEl) {
        const focusable = Array.from(dialogEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
          (el) => !el.disabled
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isProcessing, onClose]);

  const getAvailableMethods = (paymentOptions) => {
    if (!paymentOptions?.payment_methods) return [];
    const methods = [];
    const pm = paymentOptions.payment_methods;
    if (pm.credit_card) methods.push({ id: 'credit_card', label: 'Credit Card', icon: CreditCard });
    if (pm.cash_app) methods.push({ id: 'cash_app', label: 'Cash App', icon: DollarSign });
    if (pm.paypal) methods.push({ id: 'paypal', label: 'PayPal', icon: Banknote });
    if (pm.bank_transfer) methods.push({ id: 'bank_transfer', label: 'Zelle / Bank Transfer', icon: Banknote });
    return methods;
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleStripePayment = async () => {
    if (!invoice?._id) return;
    try {
      const result = await createPaymentIntent(invoice._id);
      setClientSecret(result.clientSecret);
      setSetupIntentSecret(result.setupIntentSecret || null);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handlePaySaved = async (pmId) => {
    if (!invoice?._id) return;
    setPayingWith(pmId);
    try {
      const result = await payInvoiceWithSavedCard(invoice._id, pmId);
      // Complete SCA/3DS if the card requires it (test cards won't).
      if (result.requiresAction && result.clientSecret) {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        const { error } = await stripe.confirmCardPayment(result.clientSecret);
        if (error) {
          toast.error(error.message || 'Payment could not be completed');
          setPayingWith(null);
          return;
        }
      } else if (result.status !== 'succeeded') {
        toast.error('Payment did not complete. Please try another card.');
        setPayingWith(null);
        return;
      }
      // Best-effort UI sync; the Stripe webhook is authoritative for paid state.
      try {
        await postPaymentStatus(invoice._id, {
          paymentMethodId: pmId,
          status: 'succeeded',
          type: 'one-time',
        });
      } catch {
        /* ignore: webhook still reconciles */
      }
      toast.success('Payment successful');
      handlePaymentSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Payment failed');
      setPayingWith(null);
    }
  };

  const handlePaymentSuccess = () => {
    if (onPaymentSuccess) onPaymentSuccess();
    onClose();
  };

  if (!isOpen) return null;

  const paymentOptions = invoiceDetail?.payment_options;
  const availableMethods = getAvailableMethods(paymentOptions);

  const renderMethodSelector = () => {
    if (availableMethods.length <= 1) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-6">
        {availableMethods.map((method) => {
          const Icon = method.icon;
          const isActive = activeMethod === method.id;
          return (
            <button
              key={method.id}
              onClick={() => {
                setActiveMethod(method.id);
                setClientSecret(null);
                setSetupIntentSecret(null);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${isActive
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 hover:bg-grayteck-200 dark:hover:bg-darkbg-600'
                }`}
            >
              <Icon className="w-4 h-4" />
              {method.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderCopyField = (label, value, fieldKey, link) => (
    <div className="bg-grayteck-50 dark:bg-darkbg-700 rounded-lg p-4">
      <p className="text-sm text-grayteck-500 dark:text-grayteck-400 mb-1">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <span className="text-base sm:text-lg font-semibold text-grayteck-900 dark:text-grayteck-100 select-all break-all">
          {value}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCopy(value, fieldKey)}
            className="p-2 rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 transition-colors"
            title="Copy"
          >
            {copiedField === fieldKey ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-grayteck-500 dark:text-grayteck-400" />
            )}
          </button>
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 transition-colors"
              title="Open"
            >
              <ExternalLink className="w-4 h-4 text-blue-500" />
            </a>
          )}
        </div>
      </div>
    </div>
  );

  const renderPaymentContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
        </div>
      );
    }

    if (availableMethods.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-grayteck-500 dark:text-grayteck-400">
            No payment methods are configured for this invoice. Please contact the sender.
          </p>
        </div>
      );
    }

    if (!activeMethod) {
      return (
        <div className="text-center py-4">
          <p className="text-grayteck-500 dark:text-grayteck-400">
            Select a payment method above to continue.
          </p>
        </div>
      );
    }

    switch (activeMethod) {
      case 'credit_card':
        return renderCreditCard();
      case 'cash_app':
        return renderCashApp();
      case 'paypal':
        return renderPayPal();
      case 'bank_transfer':
        return renderBankTransfer();
      default:
        return null;
    }
  };

  const renderCreditCard = () => {
    const isSubscription =
      (invoice?.invoice_type || invoiceDetail?.invoice_type) === 'subscription';

    // Saved-card quick pay (one-time invoices) unless the client picks a new card.
    if (!clientSecret && !useNewCard && !isSubscription && savedMethods.length > 0) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-grayteck-600 dark:text-grayteck-300">
            Pay ${invoice?.total?.toFixed(2)} with a saved card:
          </p>
          {savedMethods.map((pm) => (
            <button
              key={pm.pm_id}
              onClick={() => handlePaySaved(pm.pm_id)}
              disabled={!!payingWith}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-grayteck-200 dark:border-darkbg-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
            >
              <span className="flex items-center gap-2 text-grayteck-900 dark:text-grayteck-100">
                <CreditCard className="w-4 h-4 text-grayteck-500" />
                {pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} •••• {pm.last4}
                <span className="text-xs text-grayteck-500">
                  exp {String(pm.exp_month).padStart(2, '0')}/{String(pm.exp_year).slice(-2)}
                </span>
              </span>
              <span className="text-sm font-semibold text-blue-600">
                {payingWith === pm.pm_id ? 'Processing…' : `Pay $${invoice?.total?.toFixed(2)}`}
              </span>
            </button>
          ))}
          <button
            onClick={() => setUseNewCard(true)}
            disabled={!!payingWith}
            className="text-sm font-medium text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-100 disabled:opacity-50"
          >
            Use a different card
          </button>
        </div>
      );
    }

    if (!clientSecret) {
      return (
        <div className="text-center py-6">
          <CreditCard className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <p className="text-grayteck-600 dark:text-grayteck-300 mb-4">
            Pay ${invoice?.total?.toFixed(2)} securely with your credit or debit card.
          </p>
          <button
            onClick={handleStripePayment}
            disabled={isCreatingPaymentIntent}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isCreatingPaymentIntent ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Preparing...
              </span>
            ) : (
              'Continue to Payment'
            )}
          </button>
          {!isSubscription && savedMethods.length > 0 && (
            <div>
              <button
                onClick={() => setUseNewCard(false)}
                className="mt-3 text-sm font-medium text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-100"
              >
                Back to saved cards
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <PaymentForm
        clientSecret={clientSecret}
        setupIntentSecret={setupIntentSecret}
        invoiceId={invoice._id}
        invoiceType={invoice.invoice_type || invoiceDetail?.invoice_type || 'one_time'}
        email={invoice.client_email || ''}
        name={invoice.client_name || ''}
        amount={invoice.total?.toFixed(2)}
        stripePk={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
        cadence={invoiceDetail?.subscription_settings?.frequency}
        nextChargeDate={invoiceDetail?.subscription_settings?.next_charge_date}
        onSuccess={handlePaymentSuccess}
      />
    );
  };

  const renderCashApp = () => {
    const cashAppId = paymentOptions?.cash_app_id || '$grayteck';
    const cleanId = cashAppId.replace('$', '');
    return (
      <div className="space-y-4">
        {renderCopyField(
          'Cash App ID',
          cashAppId,
          'cashapp',
          `https://cash.app/$${cleanId}/${invoice?.total?.toFixed(2)}`
        )}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            Send <strong>${invoice?.total?.toFixed(2)}</strong> to the Cash App ID above,
            then your payment will be confirmed shortly.
          </p>
        </div>
        <a
          href={`https://cash.app/$${cleanId}/${invoice?.total?.toFixed(2)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-green-600 text-white
                   rounded-lg font-medium hover:bg-green-700 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Open Cash App
        </a>
      </div>
    );
  };

  const renderPayPal = () => {
    const paypalEmail = paymentOptions?.paypal_email || 'contact@graytecknologies.com';
    return (
      <div className="space-y-4">
        {renderCopyField(
          'PayPal Email',
          paypalEmail,
          'paypal',
          `https://paypal.me/grayteck/${invoice?.total?.toFixed(2)}`
        )}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            Send <strong>${invoice?.total?.toFixed(2)}</strong> to the PayPal address above,
            then your payment will be confirmed shortly.
          </p>
        </div>
        <a
          href={`https://paypal.me/grayteck/${invoice?.total?.toFixed(2)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-blue-500 text-white
                   rounded-lg font-medium hover:bg-blue-600 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Open PayPal
        </a>
      </div>
    );
  };

  const renderBankTransfer = () => {
    const bankEmail = paymentOptions?.bank_email || 'cjames@graytecknologies.com';
    return (
      <div className="space-y-4">
        {renderCopyField('Zelle / Bank Transfer Email', bankEmail, 'bank')}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            Send <strong>${invoice?.total?.toFixed(2)}</strong> via Zelle or bank transfer to the email above,
            then your payment will be confirmed shortly.
          </p>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isProcessing) onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            // max-h + a scrollable body: the subscription form (consent block, card
            // field, pay button) is tall enough to overflow a phone viewport, and
            // with overflow-hidden and no scroll the card field was simply
            // unreachable below the fold.
            className="bg-white dark:bg-darkbg-800 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-modal-title"
            tabIndex={-1}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-grayteck-200 dark:border-grayteck-700">
              <div>
                <h2 id="payment-modal-title" className="text-lg font-semibold text-grayteck-900 dark:text-grayteck-100">
                  Pay Invoice
                </h2>
                <p className="text-sm text-grayteck-500 dark:text-grayteck-400">
                  {invoice?.invoice_number} — ${invoice?.total?.toFixed(2)}
                </p>
              </div>
              <button
                onClick={() => {
                  if (!isProcessing) onClose();
                }}
                disabled={isProcessing}
                aria-label="Close"
                className="p-2 rounded-lg hover:bg-grayteck-100 dark:hover:bg-darkbg-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-grayteck-500 dark:text-grayteck-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 overflow-y-auto">
              {renderMethodSelector()}
              {renderPaymentContent()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;
